import { LitElement, css, html } from 'lit';
import { parseYouTubeId } from '@ka/contracts';
import { shared } from './shared.ts';

/**
 * Click-to-load YouTube player. Nothing is requested from YouTube until the visitor clicks.
 * Uses youtube-nocookie.com, never autoplays on page load, and always offers a watch link.
 */
export class KaYouTube extends LitElement {
  static override properties = {
    videoId: { attribute: 'video-id' },
    title: {},
    start: { type: Number },
    thumbnail: {},
    loaded: { state: true },
  };
  declare videoId: string;
  declare title: string;
  declare start: number;
  declare thumbnail: string;
  declare loaded: boolean;

  static override styles = [
    shared,
    css`
      .frame {
        position: relative;
        aspect-ratio: 16 / 9;
        background: var(--ka-edge);
        border: 3px solid var(--ka-edge);
        box-shadow: var(--ka-bevel);
      }
      iframe,
      .poster {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        border: 0;
      }
      .poster {
        display: grid;
        place-items: center;
        background: center / cover no-repeat var(--ka-stone-800);
        cursor: pointer;
        color: var(--ka-parchment);
        font: inherit;
      }
      .play {
        font: 600 1.1rem var(--ka-font-display);
        background: var(--ka-red);
        border: 2px solid var(--ka-edge);
        box-shadow: var(--ka-bevel);
        padding: 0.8em 1.4em;
      }
      .fallback {
        margin-top: 0.5rem;
        font-size: 0.9rem;
      }
    `,
  ];

  constructor() {
    super();
    this.videoId = '';
    this.title = 'Video';
    this.start = 0;
    this.thumbnail = '';
    this.loaded = false;
  }

  /** Seek by reloading with a start time (works without the IFrame API). */
  playAt(seconds: number) {
    this.start = Math.max(0, Math.floor(seconds));
    this.loaded = true;
    this.updateComplete.then(() => this.renderRoot.querySelector('iframe')?.focus());
  }

  override render() {
    const id = parseYouTubeId(this.videoId);
    if (!id) return html`<p class="panel muted">This video is not available yet.</p>`;
    const watch = `https://www.youtube.com/watch?v=${id}${this.start ? `&t=${this.start}s` : ''}`;
    const params = new URLSearchParams({ autoplay: '1', rel: '0', modestbranding: '1' });
    if (this.start) params.set('start', String(this.start));
    const poster = this.thumbnail ? `background-image:url("${encodeURI(this.thumbnail)}")` : '';
    return html`
      <div class="frame">
        ${
          this.loaded
            ? html`<iframe
                src="https://www.youtube-nocookie.com/embed/${id}?${params}"
                title=${this.title}
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowfullscreen
                referrerpolicy="strict-origin-when-cross-origin"
              ></iframe>`
            : html`<button class="poster" style=${poster} @click=${() => (this.loaded = true)}>
                <span class="play">▶ Play “${this.title}”</span>
              </button>`
        }
      </div>
      <p class="fallback muted">
        Playing loads YouTube. <a href=${watch} target="_blank" rel="noopener">Watch on YouTube</a>
      </p>
    `;
  }
}
customElements.define('ka-youtube', KaYouTube);
